<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink } from 'vue-router';
import { formatDistanceToNow, parseISO } from 'date-fns';
import Checkbox from 'primevue/checkbox';
import Tag from 'primevue/tag';
import Button from 'primevue/button';
import ExclamationTriangle from '@primeicons/vue/exclamation-triangle';
import type { ShipmentExceptionRow } from '@gpx-spa/queries/src/shipment-exception-types';
import {
  exceptionListCardTitle,
  exceptionTypeDescription,
  exceptionTypeTitle,
} from '@/utils/shipment-exception-catalog';
import {
  exceptionAccentBarClass,
  exceptionIconDisplayForSeverity,
  exceptionSeverityLabel,
} from '@/utils/shipment-exception-display';
import NamedTrackerSet from '@/components/shipment/shared/NamedTrackerSet.vue';
import type { NamedTracker } from '@/utils/named-tracker-set';
import { formatTime } from '@/filters';

const props = defineProps<{
  row: ShipmentExceptionRow;
  trackers: NamedTracker[];
  selected: boolean;
  busy?: boolean;
}>();

const emit = defineEmits<{
  toggleSelect: [];
  close: [];
  reopen: [];
}>();

const typeTitle = computed(() => exceptionTypeTitle(props.row.type));
const shipmentLabel = computed(
  () => props.row.shipment?.name || `#${props.row.shipment_id}`,
);
const cardTitle = computed(() =>
  exceptionListCardTitle(
    props.row.type,
    props.row.shipment?.name,
    props.row.shipment_id,
  ),
);

function tagSeverity(
  severity: string,
): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' {
  const s = severity.toLowerCase();
  if (s === 'high' || s === 'critical') return 'danger';
  if (s === 'medium' || s === 'warning') return 'warn';
  return 'info';
}

function exceptionCardIconClass(severity: string): string {
  return (
    exceptionIconDisplayForSeverity(severity)?.colorClass ?? 'text-neutral-600'
  );
}

function occurredDate(): Date | null {
  if (!props.row.occurred_at) return null;
  try {
    return parseISO(props.row.occurred_at);
  } catch {
    return null;
  }
}

const relativeTime = computed(() => {
  const d = occurredDate();
  return d ? formatDistanceToNow(d, { addSuffix: true }) : '—';
});

const absoluteTime = computed(() => {
  const d = occurredDate();
  return d ? (formatTime(d, 'MMM d, h:mm aa') ?? '') : '';
});
</script>

<template>
  <div
    class="relative overflow-hidden rounded-none border border-neutral-300 bg-white transition-all duration-150"
    :class="selected ? 'bg-blue-50/30 ring-1 ring-blue-200' : 'hover:shadow-md'"
  >
    <div class="flex min-h-px">
      <div
        class="w-1 shrink-0 self-stretch"
        :class="exceptionAccentBarClass(row.severity)"
        aria-hidden="true"
      />
      <div class="flex min-w-0 flex-1 items-start gap-3 p-3">
        <div class="shrink-0 pt-0.5" @click.stop>
          <Checkbox
            :model-value="selected"
            binary
            :input-id="`exception-select-${row.id}`"
            :aria-label="`Select ${cardTitle}`"
            @update:model-value="emit('toggleSelect')"
          />
        </div>

        <div class="min-w-0 flex-1">
          <div class="mb-1 flex flex-wrap items-center gap-2">
            <ExclamationTriangle
              :class="[
                'shrink-0 leading-none',
                exceptionCardIconClass(row.severity),
              ]"
              :size="14"
              aria-hidden="true"
            />
            <span class="truncate text-sm font-semibold text-neutral-900">{{
              cardTitle
            }}</span>
            <Tag
              :value="exceptionSeverityLabel(row.severity)"
              :severity="tagSeverity(row.severity)"
              class="!text-[10px] !font-semibold uppercase"
              :pt="{ root: { class: '!px-1.5 !py-0.5' } }"
            />
            <Tag
              :value="row.status === 'closed' ? 'Closed' : 'Open'"
              :severity="row.status === 'closed' ? 'success' : 'danger'"
              class="!text-[10px] !font-semibold uppercase"
              :pt="{ root: { class: '!px-1.5 !py-0.5' } }"
            />
          </div>

          <p class="mb-2 line-clamp-2 text-xs text-gray-500">
            {{ exceptionTypeDescription(row.type) }}
          </p>

          <div
            class="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-400"
          >
            <RouterLink
              class="font-medium text-blue-700 hover:underline"
              :to="{
                name: 'ShipmentDetailsView',
                params: { id: row.shipment_id },
              }"
              @click.stop
            >
              {{ shipmentLabel }}
            </RouterLink>
            <span>{{ typeTitle }}</span>
            <NamedTrackerSet :trackers="trackers" />
            <span
              class="ml-auto shrink-0 tabular-nums text-gray-500"
              :title="absoluteTime || undefined"
            >
              {{ relativeTime }}
            </span>
            <Button
              v-if="row.status === 'open'"
              label="Close"
              size="small"
              text
              class="!px-1 !py-0 !text-[11px]"
              :disabled="busy"
              @click.stop="emit('close')"
            />
            <Button
              v-else-if="row.status === 'closed'"
              label="Reopen"
              size="small"
              text
              class="!px-1 !py-0 !text-[11px]"
              :disabled="busy"
              @click.stop="emit('reopen')"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
